import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import PageHeader from "@/components/PageHeader";
import Modal from "@/components/Modal";
import { HeartPulse, CheckCircle2, Plus, UserCheck, Pencil, Trash2, Send } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "" };

export default function ProofOfLife() {
  const { t } = useI18n();
  const [pol, setPol] = useState(null);
  const [interval, setIntervalDays] = useState(90);
  const [graceDays, setGraceDays] = useState(7);
  const [requiredConfirmations, setRequiredConfirmations] = useState(2);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([
      api.get("/proof-of-life"),
      api.get("/trusted-contacts"),
    ]);
    setPol(p.data);
    setIntervalDays(p.data.interval_days);
    setGraceDays(p.data.grace_period_days || 7);
    setRequiredConfirmations(p.data.required_confirmations || 2);
    setContacts(c.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (window.location.hash === "#contacts") {
      setTimeout(() => {
        const el = document.getElementById("contacts-section");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, []);

  const checkin = async () => {
    await api.post("/proof-of-life/checkin");
    await load();
    toast.success(t("pol_confirmed_toast"));
  };

  const sendDemo = async () => {
    try {
      const r = await api.post("/pol/demo");
      toast.success(t("pol_demo_email_sent").replace("{EMAIL}", r.data.sent_to));
    } catch (e) {
      const msg = e?.response?.data?.detail || t("settings_generic_error");
      toast.error(msg);
    }
  };

  const saveSettings = async () => {
    await api.put("/proof-of-life/settings", {
      interval_days: parseInt(interval, 10),
      grace_period_days: parseInt(graceDays, 10),
      required_confirmations: parseInt(requiredConfirmations, 10),
    });
    await load();
    toast.success(t("saved"));
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.contact_id);
    setForm({ name: c.name, email: c.email, phone: c.phone || "" });
    setOpen(true);
  };

  const saveContact = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const r = await api.put(`/trusted-contacts/${editingId}`, form);
        setContacts((arr) => arr.map((x) => (x.contact_id === editingId ? r.data : x)));
      } else {
        const r = await api.post("/trusted-contacts", form);
        setContacts((arr) => [...arr, r.data]);
      }
      toast.success(t("saved"));
      setForm(EMPTY);
      setEditingId(null);
      setOpen(false);
    } catch {
      toast.error(t("settings_generic_error"));
    }
  };

  const removeContact = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/trusted-contacts/${id}`);
    toast.success(t("deleted"));
    setContacts((arr) => arr.filter((x) => x.contact_id !== id));
  };

  return (
    <div data-testid="pol-page">
      <PageHeader title={t("pol_title")} subtitle={t("pol_subtitle")} />

      <div className="glass-card rounded-md p-6 md:p-8 mb-8 gold-glow">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <HeartPulse className="w-10 h-10 md:w-12 md:h-12 text-gold" strokeWidth={1.2} />
          <div className="flex-1">
            <div className="font-display text-3xl text-gold-light mb-2">
              {pol?.status === "alive" ? t("dash_status_alive") : t("dash_status_pending")}
            </div>
            <div className="text-sm text-muted">
              {t("pol_last")}: {pol?.last_checkin ? new Date(pol.last_checkin).toLocaleString() : "—"}
            </div>
            <div className="text-sm text-muted">
              {t("pol_next")}: {pol?.next_checkin ? new Date(pol.next_checkin).toLocaleString() : "—"}
            </div>
          </div>
          <button className="btn-gold w-full md:w-auto justify-center" onClick={checkin} data-testid="pol-checkin">
            <CheckCircle2 className="w-4 h-4" /> {t("dash_pol_checkin")}
          </button>
        </div>
        <div className="divider-gold" />
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="font-label text-faded block mb-2">{t("pol_interval")}</label>
            <input
              type="number"
              min="7"
              max="365"
              className="input-field"
              value={interval}
              onChange={(e) => setIntervalDays(e.target.value)}
              data-testid="pol-interval"
            />
            <p className="text-xs text-faded mt-1">Cada cuántos días confirmar</p>
          </div>
          <div>
            <label className="font-label text-faded block mb-2">Período de gracia (días)</label>
            <input
              type="number"
              min="1"
              max="90"
              className="input-field"
              value={graceDays}
              onChange={(e) => setGraceDays(e.target.value)}
              data-testid="pol-grace"
            />
            <p className="text-xs text-faded mt-1">Antes de alertar contactos</p>
          </div>
          <div>
            <label className="font-label text-faded block mb-2">Confirmaciones</label>
            <input
              type="number"
              min="1"
              max="5"
              className="input-field"
              value={requiredConfirmations}
              onChange={(e) => setRequiredConfirmations(e.target.value)}
              data-testid="pol-required"
            />
            <p className="text-xs text-faded mt-1">Contactos para liberar legado</p>
          </div>
        </div>
        <div className="flex justify-between items-center mt-4 flex-wrap gap-3">
          <button
            className="btn-ghost text-xs inline-flex items-center gap-2"
            onClick={sendDemo}
            data-testid="pol-demo-btn"
            type="button"
          >
            <Send className="w-3.5 h-3.5" /> Enviarme una simulación
          </button>
          <button className="btn-gold" onClick={saveSettings} data-testid="pol-save">
            {t("pol_save")}
          </button>
        </div>
      </div>

      <div id="contacts-section" className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6 scroll-mt-24">
        <div>
          <h2 className="font-display text-3xl text-gold-light">{t("contacts_title")}</h2>
          <p className="text-sm text-muted mt-2 max-w-xl">{t("contacts_subtitle")}</p>
        </div>
        <button className="btn-gold" onClick={openCreate} data-testid="contact-add">
          <Plus className="w-4 h-4" /> {t("contacts_add")}
        </button>
      </div>

      {contacts.length === 0 ? (
        <div
          className="glass-card rounded-md p-8 md:p-10 border-l-4 border-l-gold"
          data-testid="contacts-empty-callout"
        >
          <div className="flex items-start gap-5">
            <div className="p-3 rounded-md bg-gold/10 border border-gold-subtle shrink-0">
              <UserCheck className="w-6 h-6 text-gold" strokeWidth={1.2} />
            </div>
            <div className="flex-1">
              <div className="font-display text-2xl text-gold-light mb-3">
                {t("contacts_empty_title")}
              </div>
              <p className="text-muted leading-relaxed mb-5">
                {t("contacts_empty_body")}
              </p>
              <button
                className="btn-gold"
                onClick={openCreate}
                data-testid="contacts-empty-cta"
              >
                <Plus className="w-4 h-4" /> {t("contacts_add")}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {contacts.map((c) => (
            <div
              key={c.contact_id}
              className="glass-card rounded-md p-5 flex items-center gap-4"
              data-testid={`contact-${c.contact_id}`}
            >
              <div className="p-3 rounded-md bg-gold/10 border border-gold-subtle">
                <UserCheck className="w-5 h-5 text-gold" strokeWidth={1.4} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-gold-light truncate">{c.name}</div>
                <div className="text-sm text-muted truncate">
                  {c.email}{c.phone ? ` · ${c.phone}` : ""}
                </div>
              </div>
              <button
                onClick={() => openEdit(c)}
                className="text-faded hover:text-gold"
                data-testid={`contact-edit-${c.contact_id}`}
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => removeContact(c.contact_id)}
                className="text-faded hover:text-red-400"
                data-testid={`contact-delete-${c.contact_id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditingId(null);
        }}
        title={editingId ? t("edit") : t("contacts_add")}
        testid="contact-modal"
      >
        <form onSubmit={saveContact} className="space-y-4">
          <div>
            <label className="font-label text-faded block mb-2">{t("name")}</label>
            <input className="input-field" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
          </div>
          <div>
            <label className="font-label text-faded block mb-2">{t("email")}</label>
            <input type="email" className="input-field" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
          </div>
          <div>
            <label className="font-label text-faded block mb-2">{t("phone")}</label>
            <input className="input-field" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>{t("cancel")}</button>
            <button type="submit" className="btn-gold" data-testid="contact-save">{t("save")}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
